import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image } from "@/components/common/image";
import { Video } from "@/components/common/video";
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/common/icon-button';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Spinner } from '../ui/modal';
import VideoController from '../video-controller';
import * as Dialog from "@radix-ui/react-dialog";

interface PreviewVideoImageProps {
  /**
   * 预览列表
   * 传递多张图片/视频URL时，开启多图/视频查看
   */
  previewList: { url: string, type: 'image' | 'video' }[];
  alt?: string;
  /** 当前预览项索引 */
  currentIndex?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** 视频图片预览组件 */
export const PreviewVideoImageFull: React.FC<PreviewVideoImageProps> = ({
  open = false,
  previewList,
  currentIndex = 0,
  alt = '查看媒体',
  onOpenChange,
}) => {

  const maskRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(currentIndex);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [load, setLoad] = useState(false);

  // 是否开启多图/视频查看
  const multiple = useMemo(() => {
    return Array.isArray(previewList) && previewList.length > 1;
  }, [previewList]);

  // 获取当前预览项
  const currentItem = useMemo(() => {
    if (Array.isArray(previewList) && previewList.length > 0) {
      return previewList[index] || previewList[0];
    }
    return null;
  }, [previewList, index]);

  // 切换上一张图片/视频
  const handlePrev = () => {
    if (multiple && Array.isArray(previewList)) {
      setIndex((prevIndex) => (prevIndex - 1 + previewList.length) % previewList.length);
    }
  };

  // 切换下一张图片/视频
  const handleNext = () => {
    if (multiple && Array.isArray(previewList)) {
      setIndex((prevIndex) => (prevIndex + 1) % previewList.length);
    }
  };

  // 点击蒙层关闭
  const handleMaskClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === maskRef.current) {
      onOpenChange?.(false);
    }
  };

  // 监听 currentIndex 变化，更新当前预览项索引
  useEffect(() => {
    if (currentIndex !== null && multiple && Array.isArray(previewList)) {
      // 确保索引在有效范围内
      setIndex(Math.max(0, Math.min(currentIndex, previewList.length - 1)));
    }
  }, [currentIndex, previewList, multiple]);

  // 自动播放视频
  useEffect(() => {
    const video = videoRef.current;
    if (!video || currentItem?.type !== 'video') return;

    const handleCanPlayThrough = async () => {
      try {
        await video.play();
      } catch (error) {
        console.warn("Auto-play failed:", error);
      }
    };

    video.addEventListener("canplaythrough", handleCanPlayThrough);

    // 如果视频已经可以播放，立即尝试播放
    if (video.readyState >= 3) {
      handleCanPlayThrough();
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [currentItem, videoRef.current]);

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowUp":
          handlePrev();
          break;
        case "ArrowDown":
          handleNext();
          break;
        case "Escape":
          onOpenChange?.(false);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!previewList || previewList.length === 0 || !currentItem) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
        <Dialog.Content>
          <div
            ref={maskRef}
            className={cn(
              "fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4 select-none",
              multiple && "p-0 bg-black"
            )}
            onClick={handleMaskClick}
          >
            {/* 根据当前项类型显示图片或视频 */}
            {currentItem.type === 'image' ? (
              <Image
                src={currentItem.url}
                alt={alt}
                className="max-w-full max-h-full object-contain"
                onDragStart={(e) => e.preventDefault()}
                loading="lazy"
              />
            ) : (
              <>
                <div
                  className="flex-1 flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute top-0 bottom-0 aspect-[9/16]">
                    <Video
                      ref={videoRef}
                      src={currentItem.url}
                      className="h-full object-contain"
                      autoPlay
                      preload="auto"
                      onClick={() => videoRef.current?.paused ? videoRef.current?.play() : videoRef.current?.pause()}
                    />
                    {/* 视频控制器 */}
                    <div className="absolute bottom-0 left-0 right-0 transition-opacity duration-250 z-[1000]" onClick={(e) => e.stopPropagation()}>
                      <VideoController videoRef={videoRef} onLoad={setLoad} />
                    </div>
                  </div>
                </div>
                {load && (
                  <div className="absolute inset-0 flex justify-center items-center bg-black/20">
                    <Spinner className="text-white" size={32} />
                  </div>
                )}
              </>
            )}

            {/* 控制按钮 */}
            <>
              <IconButton
                icon={<X className="size-5 text-gray-500" />}
                className='size-10 absolute top-8 right-8 bg-white hover:bg-white/80 border border-[rgba(208,213,221,1)] rounded-[8px]'
                onClick={() => onOpenChange?.(false)}
              />

              {/* 只有多项时显示上下切换按钮 */}
              {multiple && (
                <div className="absolute top-1/2 right-8 transform -translate-y-1/2 space-y-6">
                  <IconButton
                    icon={<ChevronUp className="size-6 text-white" />}
                    className='size-10 bg-white/30 hover:bg-[rgba(255,255,255,0.2)] rounded-[10px]'
                    onClick={handlePrev}
                  />
                  <IconButton
                    icon={<ChevronDown className="size-6 text-white" />}
                    className='size-10 bg-white/30 hover:bg-[rgba(255,255,255,0.2)] rounded-[10px]'
                    onClick={handleNext}
                  />
                </div>
              )}
            </>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

  )
};